// kfe-pathway-list.js — flat-list pathway renderer + content-folder drilldown.
//
// PORT TARGET (iOS canon, read 2026-06-19 per Rule #9):
//   iEnglish/Classes/KFEModules/Main/TableListViewController.m
//
// The eight study paths split into two iOS view controllers by SHAPE:
//   • squiggle (stages → lessons)  → StagesViewController.m   → <kfe-pathway>
//   • flat menu (`items`)          → TableListViewController.m → THIS component
// `<kfe-pathway>` mounts this component at "/" when the active pathway is flat
// (no `stages`, has `items`) — see kfe-pathway.js's dispatch fork (Brief 168).
//
// ROW LAYOUT (KFELessonCell, from TableListViewController.m -cellForRow…):
//   iOS picks a cell by two axes — has-description? and has-requiredPlay? —
//   yielding {With,Without}Circle × {Description?,}. The right icon is one of:
//     • KFELessonCellRightIconWhiteTick   completedCount >= requiredPlay  → ✓
//     • KFELessonCellRightIconNumber      "completedCount/requiredPlay"   → arc
//     • KFELessonCellRightIconNone        no requiredPlay                 → empty
//   We mirror that exactly for LEAF rows (the arc is <kfe-play-progress>, same as
//   lesson-detail). A `content` row is a FOLDER (iOS `model.items.count` → push a
//   new TableListViewController) — we render a › chevron drilldown affordance and
//   navigate to /folder/:id rather than an exercise.
//
// ROW TAP DISPATCH (TableListViewController.m -didSelectRowAtIndexPath:): iOS
//   branches on plist attributes (forRecord / L2BType / MCType / TFType / media /
//   test / items.count). The web equivalent is already centralised in
//   <kfe-exercise-host>'s type switch, so a leaf row simply navigates to
//   /exercise/:id and the host picks the screen by the item's content type
//   (resolved from kfe_content_items.json). A `content` folder row → /folder/:id
//   (this component, re-entered with `folderId`). `mmu_media` leaves (180_days's
//   raw word-media units — image+audio MMUs with no kfe_content_items entry, and
//   so no /exercise screen) render as flagged, non-navigable rows: a known
//   content-shape gap awaiting an MMU-viewer brief, NOT a code bug.
//
// GATING: TableListViewController gates NOTHING — its
//   `areAllPrecedingLessonsCompletedForLessonId:` is commented out, and the flat
//   pathways carry no `excludeFromGating`/gate entries (Brief 157 audit). So every
//   row is unlocked. (The squiggle's gating lives entirely in <kfe-pathway>.)
//
// COMPLETION: per-row leaf completion is the SAME source the rest of the app uses
//   — learner-store itemProgress.completedCount vs the row's authored
//   requiredPlay (Brief 100, carried in pathway.json / folders.json). Folder
//   rollup completion (iOS `updateFolderCompletedState`) reads the Brief 163
//   roll-up cache if an entry exists; absent → incomplete (no eager deep derive —
//   180_days folders fan out to ~12k leaves; a follow-up can populate the cache).

import { KFEComponent } from "../core/component.js";
import { html } from "../core/html.js";
import { t, getLanguage } from "../core/i18n.js";
import { services } from "../platform-services.js";
import { navigate } from "../router.js";
import { loadPathway, filterGates } from "../data/pathway-loader.js";
import { loadFolder } from "../data/folder-loader.js";
import { getActivePathwayId } from "../core/active-pathway.js";

/**
 * Row type → chip {label, color}. Mirrors kfe-lesson-detail's TYPE_CHIP hues so
 * a row reads consistently whether reached via the squiggle or a flat list.
 * `content` (folder) + `mmu_media` (flagged leaf) are list-only additions.
 * @type {Record<string, {label: string, color: string}>}
 */
const TYPE_CHIP = {
  content:            { label: "Folder",   color: "#2B4C7E" },
  l2b_exercise:       { label: "L2B",      color: "#27ae60" },
  l2b_test:           { label: "L2T",      color: "#27ae60" },
  haai_exercise:      { label: "HAAI",     color: "#e67e22" },
  recording_exercise: { label: "SR",       color: "#8e44ad" },
  true_false:         { label: "T/F",      color: "#8e44ad" },
  multiple_choice:    { label: "MC",       color: "#2c3e50" },
  situation_dialogue: { label: "Dialogue", color: "#3D6098" },
  music_track:        { label: "Music",    color: "#3D6098" },
  audio_video:        { label: "Audio",    color: "#3D6098" },
  video_lesson:       { label: "Video",    color: "#3D6098" },
  reading_lesson:     { label: "Read",     color: "#3D6098" },
  reference_note:     { label: "Note",     color: "#3D6098" },
  mmu_media:          { label: "Word",     color: "#7f8c8d" },
  unknown:            { label: "?",        color: "var(--kfe-muted)" },
};

/**
 * @typedef {object} ListItem
 * @property {string} id
 * @property {string} [type]
 * @property {boolean} [isFolder]
 * @property {string} [titleEn]
 * @property {string} [titleZh]
 * @property {number} [requiredPlay]
 */

export class KFEPathwayList extends KFEComponent {
  // `pathwayId` → render that pathway's top-level `items`.
  // `folderId`  → drilldown: render the folder's children (active pathway's
  //               folders.json). Exactly one is set per mount.
  static props = ["pathwayId", "folderId"];
  static styles = `
    :host { display: block; }
    .item-list { list-style: none; margin: 0; padding: 0; }

    .item-row {
      display: flex; align-items: center; gap: 0.75rem;
      padding: 0.875rem 1rem;
      background: var(--kfe-surface);
      border-bottom: 1px solid var(--kfe-border);
      cursor: pointer;
      color: var(--kfe-fg); font-family: var(--kfe-font);
    }
    .item-row:hover { background: var(--kfe-surface-hover); }
    .item-row:focus-visible { outline: 2px solid var(--kfe-accent); outline-offset: -2px; }
    /* mmu_media leaves have no destination screen yet — render inert + dimmed
       so the row reads as "not openable" rather than broken (content gap). */
    .item-row.inert { cursor: default; opacity: 0.6; }
    .item-row.inert:hover { background: var(--kfe-surface); }

    .item-main { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
    .item-title { color: var(--kfe-fg); font-weight: 600; overflow-wrap: anywhere; }

    .item-type {
      color: #fff;
      font-size: 0.75rem; font-weight: 600;
      text-transform: uppercase;
      padding: 2px 8px; border-radius: 4px;
      white-space: nowrap; flex: 0 0 auto;
    }

    /* Trailing slot: ✓ tick, an in-progress arc, a › drilldown chevron, or empty
       — fixed min width so edges align across mixed rows. */
    .item-progress {
      flex: 0 0 auto; min-width: 2.25rem;
      display: inline-flex; align-items: center; justify-content: center; gap: 0.4rem;
    }
    .item-tick { color: var(--kfe-success); font-weight: 700; font-size: 1.1rem; }
    .item-chevron { color: var(--kfe-muted); font-size: 1.3rem; line-height: 1; }

    .empty { color: var(--kfe-muted); padding: 2rem; text-align: center; }
  `;

  /** @type {ListItem[]} */
  _items = [];
  /** Completable-leaf progress: id → {completed, required}. @type {Map<string,{completed:number,required:number}>} */
  _progress = new Map();
  /** Roll-up-complete folder ids (Brief 163 cache). @type {Set<string>} */
  _foldersComplete = new Set();
  /** False until the first load resolves — distinguishes loading from empty. */
  _loaded = false;

  async onConnected() {
    const onI18n = () => this._scheduleRender();
    document.addEventListener("kfe:i18n", onI18n);
    this.addCleanup(() => document.removeEventListener("kfe:i18n", onI18n));

    // A drilled-in folder view bounces home on a pathway switch (its folder map
    // belongs to the old pathway). The top-level list (pathwayId set) is owned by
    // <kfe-pathway>, which re-decides the squiggle/list fork on the same event —
    // so only the folder instance needs to act, avoiding a double reload.
    const onPathwayChanged = () => {
      if (/** @type {any} */ (this).folderId) navigate("/");
    };
    document.addEventListener("kfe:pathway-changed", onPathwayChanged);
    this.addCleanup(() => document.removeEventListener("kfe:pathway-changed", onPathwayChanged));

    // Re-read the just-completed item's progress and repaint its row (mirrors
    // lesson-detail). The event bubbles to document from learner-store.
    const onItemCompleted = (/** @type {Event} */ e) => {
      const id = /** @type {CustomEvent} */ (e).detail?.itemId;
      void this._refreshProgress(id);
    };
    document.addEventListener("kfe:item-completed", onItemCompleted);
    this.addCleanup(() => document.removeEventListener("kfe:item-completed", onItemCompleted));

    await this._load();
  }

  async _load() {
    /** @type {ListItem[]} */
    let items = [];
    let title = t("pathway.title");
    try {
      const folderId = /** @type {string|undefined} */ (/** @type {any} */ (this).folderId);
      if (folderId) {
        const pathwayId = getActivePathwayId();
        const folder = await loadFolder(pathwayId, String(folderId).replace(/^item_/, ""));
        if (folder) {
          items = folder.items || [];
          title = this._localeTitle(folder) || title;
        } else {
          console.warn(`[pathway-list] folder not found: ${pathwayId}/${folderId}`);
        }
      } else {
        const pathwayId = /** @type {string} */ (/** @type {any} */ (this).pathwayId) || getActivePathwayId();
        const pathway = /** @type {any} */ (await loadPathway(pathwayId));
        items = filterGates(pathway.items || []);
        title = this._localeTitle(pathway) || title;
      }
    } catch (e) {
      console.warn("[pathway-list] load failed", e);
    }

    this._items = items;
    this.dispatchEvent(new CustomEvent("kfe:set-header-title", {
      detail: { title },
      bubbles: true,
      composed: true,
    }));

    await this._loadProgress();
    this._loaded = true;
    this._scheduleRender();
  }

  /**
   * Read leaf completion (itemProgress) for every row that carries a
   * requiredPlay, and the roll-up cache for folder rows. One pass; cheap reads.
   */
  async _loadProgress() {
    /** @type {Map<string,{completed:number,required:number}>} */
    const progress = new Map();
    await Promise.all(
      this._items
        .filter((it) => !it.isFolder && typeof it.requiredPlay === "number" && it.requiredPlay > 0)
        .map(async (it) => {
          const numId = Number(String(it.id).replace(/^item_/, ""));
          if (!Number.isFinite(numId)) return;
          try {
            const prog = /** @type {any} */ (await services.learner.getItemProgress(numId));
            progress.set(String(it.id), {
              completed: prog?.completedCount ?? 0,
              required: /** @type {number} */ (it.requiredPlay),
            });
          } catch { /* default incomplete */ }
        }),
    );
    this._progress = progress;

    // Folder roll-up completion from the Brief 163 cache (read-only; no derive).
    /** @type {Set<string>} */
    const complete = new Set();
    const folderIds = this._items.filter((it) => it.isFolder).map((it) => String(it.id).replace(/^item_/, ""));
    if (folderIds.length) {
      try {
        const rows = await /** @type {any} */ (services.learner).getAllRollupCache();
        const byId = new Map(rows.map((/** @type {any} */ r) => [String(r.id), r]));
        for (const fid of folderIds) {
          const c = byId.get(fid);
          if (c && c.complete) complete.add(fid);
        }
      } catch { /* cache absent → all incomplete */ }
    }
    this._foldersComplete = complete;
  }

  /** Re-read one completed leaf's progress and repaint if it changed. @param {string|number} itemId */
  async _refreshProgress(itemId) {
    if (itemId == null) return;
    const key = `item_${String(itemId).replace(/^item_/, "")}`;
    const row = this._items.find((it) => String(it.id) === key);
    if (!row || row.isFolder || typeof row.requiredPlay !== "number") return;
    try {
      const prog = /** @type {any} */ (await services.learner.getItemProgress(Number(String(itemId).replace(/^item_/, ""))));
      this._progress.set(key, { completed: prog?.completedCount ?? 0, required: row.requiredPlay });
      this._scheduleRender();
    } catch { /* leave prior state */ }
  }

  /** @param {{titleEn?: string, titleZh?: string}} obj */
  _localeTitle(obj) {
    const lang = getLanguage();
    return (lang === "zh-Hans" && obj.titleZh) ? obj.titleZh : (obj.titleEn || "");
  }

  /** @param {ListItem} item */
  _open(item) {
    const bareId = String(item.id).replace(/^item_/, "");
    if (item.isFolder || item.type === "content") {
      navigate(`/folder/${bareId}`);
    } else if (item.type === "mmu_media") {
      // No destination yet — inert (handled by render: row not wired).
    } else {
      navigate(`/exercise/${bareId}`);
    }
  }

  /** @param {ListItem} item */
  _renderRow(item) {
    const title = this._localeTitle(item) || String(item.id);
    const chip = TYPE_CHIP[String(item.type)] || TYPE_CHIP.unknown;
    const isFolder = item.isFolder === true || item.type === "content";
    const isInert = item.type === "mmu_media";

    const li = document.createElement("li");
    li.className = "item-row" + (isInert ? " inert" : "");
    if (!isInert) {
      li.setAttribute("role", "button");
      li.setAttribute("tabindex", "0");
      const open = () => this._open(item);
      li.addEventListener("click", open);
      li.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); open(); }
      });
    }

    const main = document.createElement("div");
    main.className = "item-main";
    const titleEl = document.createElement("span");
    titleEl.className = "item-title";
    titleEl.textContent = title;
    main.appendChild(titleEl);

    const chipEl = document.createElement("span");
    chipEl.className = "item-type";
    chipEl.style.background = chip.color;
    chipEl.textContent = chip.label;

    const progress = document.createElement("span");
    progress.className = "item-progress";
    if (isFolder) {
      // Folder roll-up tick (iOS updateFolderCompletedState) when the cache says
      // so, then the › drilldown chevron (iOS pushes a child TableListVC).
      if (this._foldersComplete.has(String(item.id).replace(/^item_/, ""))) {
        const tick = document.createElement("span");
        tick.className = "item-tick";
        tick.textContent = "✓";
        progress.appendChild(tick);
      }
      const chevron = document.createElement("span");
      chevron.className = "item-chevron";
      chevron.textContent = "›";
      progress.appendChild(chevron);
    } else {
      // Leaf: iOS WithCircle/WithoutCircle by requiredPlay (Brief 100).
      const prog = this._progress.get(String(item.id));
      if (prog) {
        if (prog.completed >= prog.required) {
          const tick = document.createElement("span");
          tick.className = "item-tick";
          tick.textContent = "✓";
          progress.appendChild(tick);
        } else {
          const arc = document.createElement("kfe-play-progress");
          /** @type {any} */ (arc).completed = prog.completed;
          /** @type {any} */ (arc).required = prog.required;
          progress.appendChild(arc);
        }
      }
      // else: no requiredPlay → WithoutCircle (empty slot), matching iOS.
    }

    li.append(main, chipEl, progress);
    return li;
  }

  render() {
    if (!this._loaded) {
      return html`<div class="empty">${t("loading")}</div>`;
    }
    if (this._items.length === 0) {
      return html`<div class="empty">${t("pathway.empty")}</div>`;
    }
    return html`<ul class="item-list">${this._items.map((it) => this._renderRow(it))}</ul>`;
  }
}
